import {
  Backdrop,
  Button,
  ButtonGroup,
  Container,
  Grid,
  Tab,
  Tabs,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { AppState } from "../reducers";
import CenterDomainScatterChartTabs from "../components/CenterDomainScatterChartTabs";
import { ChartMode } from "../components/CenterTrendScatterChart";
import { StyledGridHeader } from "../StyledComponents";
import {
  CenterUserMissionScoreSnapshotData,
  getCenterMissionScoreSnapshot,
} from "../api";
import { firebaseUser } from "../firebase";

export default function CenterMissionTab() {
  const [showChart, setShowChart] = useState<boolean>(false);
  const [rawData, setRawData] = useState<{
    [key: string]: CenterUserMissionScoreSnapshotData;
  }>({});
  const loggedInUser = useSelector<AppState, firebaseUser | null>(
    (state) => state.loggedInUser
  );

  // const [scoreChartDisplayMode, setScoreChartDisplayMode] = useState<"score">("score");
  const [timeChartDisplayMode, setTimeChartDisplayMode] = useState<
    "time" | "time_acc"
  >("time");

  const [dataForScoreChart, setDataForScoreChart] =
    useState<CenterUserMissionScoreSnapshotData>();
  const [dataForTimeChart, setDataForTimeChart] =
    useState<CenterUserMissionScoreSnapshotData>();

  const [tabNumberCenterMode, setTabNumberCenterMode] = useState<number>(0);
  const { t } = useTranslation();

  const onClickShowChartButton = async () => {
    setShowChart(true);
  };

  const loadDataOfSpecificMonth = async (
    year: string,
    month: string
  ): Promise<CenterUserMissionScoreSnapshotData | undefined> => {
    if (!loggedInUser) return undefined;
    const result = await getCenterMissionScoreSnapshot(
      loggedInUser.uid,
      year,
      month
    );
    if (!result) return undefined;

    const rawDataKey: string = `${year}-${month}`;

    if (!rawData) setRawData({});

    setRawData({
      ...rawData,
      [rawDataKey]: result!,
    });
    return result;
  };

  const handleTimeUnitChange = async (
    mode: ChartMode,
    year: string,
    month: string
  ) => {
    // Check cache, if cache exist then set the cache
    const rawDataKey: string = `${year}-${month}`;
    if (rawData[rawDataKey]) {
      if (mode === "time" || mode === "time_acc")
        setDataForTimeChart(rawData[rawDataKey]);
      else if (mode === "score") setDataForScoreChart(rawData[rawDataKey]);
    } else {
      if (mode === "time" || mode === "time_acc")
        setDataForTimeChart(await loadDataOfSpecificMonth(year, month));
      else if (mode === "score")
        setDataForScoreChart(await loadDataOfSpecificMonth(year, month));
    }
  };

  // useEffect(() => {
  //   console.log(timeChartDisplayMode);
  // }, [timeChartDisplayMode])

  return (
    <>
      <Grid item sm={9} xs={12} style={{ paddingTop: 30 }}>
        <Tabs
          value={tabNumberCenterMode}
          onChange={(e, v) => setTabNumberCenterMode(v)}
          indicatorColor="primary"
          textColor="primary"
          centered
        >
          <Tab label={t("center_mission_tab.label_game_score")} />
          <Tab label={t("center_mission_tab.label_game_time")} />
        </Tabs>
        {tabNumberCenterMode === 0 ? (
          <CenterScoreTab
            showChart={showChart}
            data={dataForScoreChart}
            onClickShowChartButton={onClickShowChartButton}
            handleTimeUnitChange={handleTimeUnitChange}
          />
        ) : tabNumberCenterMode === 1 ? (
          <CenterTimeTab
            showChart={showChart}
            data={dataForTimeChart}
            onClickShowChartButton={onClickShowChartButton}
            handleTimeUnitChange={handleTimeUnitChange}
          />
        ) : null}
      </Grid>
    </>
  );
}

const CenterScoreTab = (props: {
  showChart: boolean;
  data: CenterUserMissionScoreSnapshotData | undefined;
  onClickShowChartButton: () => void;
  handleTimeUnitChange: (mode: ChartMode, year: string, month: string) => void;
}) => {
  const [displayMode, setDisplayMode] = useState<"score">("score");
  const { t } = useTranslation();

  return (
    <Container>
      <Container
        style={{
          background: "#FAFAFA",
          border: 0,
          borderRadius: 5,
          boxShadow: "0 0 50px 0px rgba(0, 0, 0, .15)",
          padding: "50px",
          margin: "50px 0",
          position: "relative",
        }}
      >
        <StyledGridHeader
          style={{
            textAlign: "left",
            paddingLeft: 30,
            paddingTop: 0,
            marginBottom: 50,
          }}
        >
          {t("center_mission_tab.score_tab_header")}
        </StyledGridHeader>
        {props.showChart && (
          <CenterDomainScatterChartTabs
            mode={displayMode}
            data={props.data}
            onChangeTimeUnit={props.handleTimeUnitChange}
          />
        )}
        <Backdrop
          style={{
            position: "absolute",
            zIndex: 9,
            background: "rgba(255,255,255,0.8)",
          }}
          open={!props.showChart}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={props.onClickShowChartButton}
          >
            {t("center_mission_tab.score_tab_butt_show_chart")}
          </Button>
        </Backdrop>
      </Container>
    </Container>
  );
};

const CenterTimeTab = (props: {
  showChart: boolean;
  data: CenterUserMissionScoreSnapshotData | undefined;
  onClickShowChartButton: () => void;
  handleTimeUnitChange: (mode: ChartMode, year: string, month: string) => void;
}) => {
  const [displayMode, setDisplayMode] = useState<"time" | "time_acc">("time");
  const { t } = useTranslation();

  return (
    <Container>
      <Container
        style={{
          background: "#FAFAFA",
          border: 0,
          borderRadius: 5,
          boxShadow: "0 0 50px 0px rgba(0, 0, 0, .15)",
          padding: "50px",
          margin: "50px 0",
          position: "relative",
        }}
      >
        <StyledGridHeader
          style={{
            textAlign: "left",
            paddingLeft: 30,
            paddingTop: 0,
            marginBottom: 50,
            position: "relative",
          }}
        >
          {t("center_mission_tab.time_tab_header")}
          <ButtonGroup
            style={{ position: "absolute", right: 0, top: 0 }}
            color="primary"
            size="large"
            aria-label="large outlined secondary button group"
          >
            <Button
              onClick={() => setDisplayMode("time")}
              style={{
                backgroundColor: displayMode === "time" ? "#093a4f" : "#ffffff",
                color: displayMode === "time" ? "#ffffff" : "#093a4f",
              }}
            >
              {t("center_mission_tab.time_tab_time")}
            </Button>
            <Button
              onClick={() => setDisplayMode("time_acc")}
              style={{
                backgroundColor:
                  displayMode === "time_acc" ? "#093a4f" : "#ffffff",
                color: displayMode === "time_acc" ? "#ffffff" : "#093a4f",
              }}
            >
              {t("center_mission_tab.time_tab_time_accumulative")}
            </Button>
          </ButtonGroup>
        </StyledGridHeader>
        {props.showChart && (
          <CenterDomainScatterChartTabs
            mode={displayMode}
            data={props.data}
            onChangeTimeUnit={props.handleTimeUnitChange}
          />
        )}
        <Backdrop
          style={{
            position: "absolute",
            zIndex: 9,
            background: "rgba(255,255,255,0.8)",
          }}
          open={!props.showChart}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={props.onClickShowChartButton}
          >
            {t("center_mission_tab.time_tab_butt_show_chart")}
          </Button>
        </Backdrop>
      </Container>
    </Container>
  );
};
